import {
    ChangeEvent,
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react'

import {
    Button,
    ErrorLabel,
    InfoListItem,
    LoadingSpinner,
} from '@/components'
import { TextInput } from '@/compositions'
import { trans } from '@/helpers/trans'

import './ProfileDetailForm.scss'

const emailRegEx = /[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/
const EMAIL = 'email'
const PHONE = 'tel'

export interface ProfileDetailField {
    propName: string
    type?: string
    value?: string
}

interface ProfileDetailFormProps {
    title: string
    fields: ProfileDetailField[]
    onSave: (values: string[]) => void
    intro?: string | null
    loading: boolean
}

export const ProfileDetailForm: FC<ProfileDetailFormProps> = ({
    title,
    fields,
    onSave,
    intro = null,
    loading,
}): ReactElement => {
    const [emailCheck, setEmailCheck] = useState(true)
    const [phoneCheck, setPhoneCheck] = useState(true)
    const [formCompleted, setFormCompleted] = useState(true)
    const [formModified, setFormModified] = useState(false)
    // TODO: checks emailCheck twice and never phoneCheck, so an invalid phone number can still be saved
    const canSubmit = emailCheck && emailCheck && formCompleted
    const formRef = useRef<HTMLFormElement>(null)

    const getInputs = (): HTMLInputElement[] => Array.from(formRef.current?.getElementsByTagName('input') || [])

    const invalidFields = (inputs: HTMLInputElement[]): HTMLInputElement | undefined => inputs.find((input) => {
        if (input.name === trans('pages.profile.houseNumberAddition')) {
            return false
        }

        return input.value.length === 0
    })

    const onFormChange = (event: ChangeEvent<HTMLFormElement>): void => {
        const { target } = event

        if (target instanceof HTMLInputElement && target.type === EMAIL) {
            setEmailCheck(target.value.toLowerCase().search(emailRegEx) === 0)
        }

        if (target instanceof HTMLInputElement && target.type === PHONE) {
            setPhoneCheck(target.value.length >= 10)
        }

        setFormCompleted(! invalidFields(getInputs()))
        setFormModified(true)
    }

    const onFormSubmit = (event: FormEvent): void => {
        event.preventDefault()
        onSave(getInputs().map(input => input.value))
    }

    const getInvalidCheck = (type?: string): boolean => {
        if (type === EMAIL) {
            return ! emailCheck
        }

        if (type === PHONE) {
            return ! phoneCheck
        }

        return false
    }

    const getFeedback = (type?: string): string | null => {
        if (type === EMAIL) {
            return trans('pages.profile.emailFeedback')
        }

        if (type === PHONE) {
            return trans('pages.profile.phoneFeedback')
        }

        return null
    }

    return (
        <form
            className="profile-page-detail"
            ref={formRef}
            onSubmit={onFormSubmit}
            onChange={onFormChange}
        >
            <div className="profile-page-detail__header">
                {title}
            </div>
            { intro && (
                <div className="profile-page-detail__intro">
                    {intro}
                </div>
            )}
            <ul className="profile-page-detail__list-group">
                { fields.map(field => (
                    <InfoListItem
                        key={field.propName}
                        label={field.propName}
                        className="profile-page-detail__list-item"
                        childrenWrapperClassName="profile-page-detail__list-item-children"
                        labelClassName="profile-page-detail__list-item-label"
                    >
                        <div className="profile-page-detail__form-group">
                            <TextInput
                                error={getInvalidCheck(field.type) ? getFeedback(field.type) || undefined : undefined}
                                id={field.propName}
                                name={field.propName}
                                type={field.type || 'text'}
                                defaultValue={field.value}
                                placeholder={field.propName}
                            />
                            {/* TODO: ErrorLabel only renders `text`, so this requirements label has always been empty */}
                            <ErrorLabel text="" className="profile-page-detail__form-requirements" />
                        </div>
                    </InfoListItem>
                ))}
            </ul>
            <div className="profile-page-detail__save-button-wrapper">
                <Button
                    color="orange"
                    disabled={! canSubmit || ! formModified}
                    text={trans('common.save')}
                    type="submit"
                    className="profile-page-detail__save-button"
                />
            </div>
            {loading && <LoadingSpinner />}
        </form>
    )
}
